<template>
  <poppins-dropdown
    :id="id"
    :label-name="labelName"
    :options="options"
    :model-value="dropdownValue"
    :status="status"
    :helper-text="helperText"
    :required="required"
    :disabled="disabled || null"
    class="dropdown-icon-overridden"
    name="timePickerDropdown"
    @update:model-value="dropdownSelectionChanged"
  >
    <icon-time class="dropdown-icon" />
  </poppins-dropdown>
</template>

<script lang="ts">
import { addMinutes, format, formatISO, isBefore, isSameDay, parseISO } from 'date-fns';
import Dropdown from '../dropdown/Dropdown.vue';
import SelectionOption from '../../helpers/selection';
import helperStatus from '../../helpers/status';
import IconTime from '../../icons/IconTime.vue';

const CLEAR_OPTION_VALUE = '__clear__';

/**
 * TimePicker provides a consistent way for us to allow users to choose times. It encapsulates the logic to generate the options and display text.
 * TimePicker is essentially a specialized Dropdown wrapper.
 * You can find it used in Search filters at https://www.sittercity.com/availability/search?zipcode=60601&careType=babysitter and in the Job post flow at https://www.sittercity.com/job-post/schedule.
 * TimePicker should be getting a Date, but converting it to String for its child component, Dropdown.
 */

export default {
  name: 'TimePicker',
  components: {
    PoppinsDropdown: Dropdown,
    IconTime,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    // accessibility
    /**
     * Label will move into the placeholder slot if there is no value
     */
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    /**
     * Id is necessary for screenreader to be able to read the label
     */
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    // values
    /**
     * Start is passed in as a full timestamp that includes the date, but is usually displayed as HH:MM am/pm. You can access the day's date, though.
     */
    start: {
      type: Date,
      default: null,
    },
    /**
     * End is passed in as a full timestamp that includes the date, but is usually displayed as HH:MM am/pm. You can access the day's date, though.
     * End input should never display times that happen before the start time.
     */
    end: {
      type: Date,
      default: null,
    },
    /**
     * Value is usually a full timestamp string that includes the date.
     */
    modelValue: {
      type: Date,
      default: null,
    },
    /**
     * Times are usually generated in 30 minute-increments
     */
    step: {
      type: Number,
      default: 30,
    },
    // validation
    /**
     * Status determines whether TimePicker is in error state or not.
     */
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    /**
     * Helper text displays what the error message is
     */
    helperText: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * Selection array displays label at the top of the list to give the user some context.
       * It has separate display and value properties because that's what SelectionOption array expects.
       */
      firstSelectionOption: {
        display: this.labelName,
        value: CLEAR_OPTION_VALUE,
      },
    };
  },
  computed: {
    /**
     * This is the value being passed into the child component, Dropdown
     */
    dropdownValue(): string {
      if (!this.modelValue) {
        return '';
      }

      const matchingOption = this.options.find((option) => {
        if (typeof option.value !== 'string' || !option.value || option.value === CLEAR_OPTION_VALUE) return false;

        return parseISO(option.value).getTime() === this.modelValue.getTime();
      });

      if (matchingOption && typeof matchingOption.value === 'string') {
        return matchingOption.value;
      }

      return formatISO(this.modelValue);
    },
    /**
     * Generates Dropdown times
     */
    options(): SelectionOption[] {
      const times: Array<SelectionOption> = [];

      // Selection array displays label at the top of the list to give the user some context
      times.push(this.firstSelectionOption);

      // return empty selection if we can't generate a time range
      if (!this.start || !this.end) return times;

      let t = this.start;

      while (isBefore(t, this.end)) {
        const formattedTime = format(t, 'h:mm aaa');
        if (isSameDay(t, this.start)) {
          times.push({
            display: formattedTime,
            value: formatISO(t),
          });
        } else {
          times.push({
            display: `${formattedTime} (next day)`,
            value: formatISO(t),
          });
        }
        t = addMinutes(t, this.step);
      }

      this.ensureSelectedOption(times);

      return times;
    },
  },
  methods: {
    /**
     * Guarantees the currently selected value is always present in the option list.
     * When the modelValue falls outside the generated [start, end] range (for example an
     * end time that is now earlier than a freshly chosen start), it is inserted in
     * chronological order so the controlled <select> can render it instead of going blank.
     */
    ensureSelectedOption(times: SelectionOption[]): void {
      if (!this.modelValue) return;

      const target = this.modelValue.getTime();
      const alreadyPresent = times.some(
        (option) =>
          typeof option.value === 'string' && option.value !== CLEAR_OPTION_VALUE && parseISO(option.value).getTime() === target
      );
      if (alreadyPresent) return;

      const formattedTime = format(this.modelValue, 'h:mm aaa');
      const sameDay = this.start ? isSameDay(this.modelValue, this.start) : true;
      const selectedOption: SelectionOption = {
        display: sameDay ? formattedTime : `${formattedTime} (next day)`,
        value: formatISO(this.modelValue),
      };

      // Keep the leading label option first, then insert in chronological order.
      let insertIndex = times.length;
      for (let i = 1; i < times.length; i += 1) {
        const value = times[i].value;
        if (typeof value === 'string' && value !== CLEAR_OPTION_VALUE && parseISO(value).getTime() > target) {
          insertIndex = i;
          break;
        }
      }

      times.splice(insertIndex, 0, selectedOption);
    },
    dropdownSelectionChanged(newValue: string) {
      if (!newValue || newValue === CLEAR_OPTION_VALUE) {
        this.$emit('update:modelValue', null);
        return;
      }

      // dropdownValue is a string for Dropdown, but needs to be a Date for its parent components.
      const stringToDate = parseISO(newValue);

      this.$emit('update:modelValue', stringToDate);
    },
  },
};
</script>
